import { gql } from '@src/lib/gql'

/**
 * Pitch Deck Auditor GraphQL operations.
 *
 * Authored as inline `gql` documents (no codegen hooks in this app — see useAiAction.ts).
 * Consumed with the generic useQuery / useMutation / useSubscription from @apollo/client/react.
 * Fragments are interpolated with `${FRAGMENT}` (fraql), mirroring src/hooks/useAiAction.ts.
 */

/** Self-contained audit-slide selection (issues + whyThisWorks live in `items`). */
export const PITCH_DECK_AUDIT_SLIDE_FIELDS = gql`
  fragment PitchDeckAuditorAuditSlideFields on ScoPitchDeckAuditSlide {
    id
    pitchDeckAuditSlideId
    pitchDeckSlideId
    slideKey
    slideLabel
    slideCode
    sortOrder
    deckPosition
    ratingKey
    ratingLabel
    score
    scoreMethodKey
    whatTheySaid
    whatWorks
    whatsMissingOrBroken
    investorObjection
    suggestedRewrite
    acceptedRewrite
    acceptedAt
    coherenceNote
    items {
      id
      pitchDeckAuditSlideItemId
      itemTypeKey
      body
      sortOrder
      ruleId
      ruleWeight
      targetPhrase
    }
  }
`

/** Parsed slide (nav identity + a thin pointer to its latest audit slide for the rail). */
export const PITCH_DECK_SLIDE_FIELDS = gql`
  fragment PitchDeckAuditorSlideFields on ScoPitchDeckSlide {
    id
    pitchDeckSlideId
    pitchDeckId
    position
    canonicalPosition
    classificationKey
    slideName
    slideTitle
    slideCode
    currentText
    bodyText
    slideImageUrl
    toolSession {
      id
      toolSessionId
      threadId
    }
    latestPitchDeckAuditSlide {
      id
      pitchDeckAuditSlideId
      slideKey
      ratingKey
      ratingLabel
      score
    }
  }
`

/** Audit head (deck-level scoring + the full per-slide audit rows). */
export const PITCH_DECK_AUDIT_FIELDS = gql`
  fragment PitchDeckAuditorAuditFields on ScoPitchDeckAudit {
    id
    pitchDeckAuditId
    pitchDeckId
    statusKey
    auditTypeKey
    companyName
    overallScore
    scoreMethodKey
    wilVerdict
    overallSummary
    crossSlideCoherenceAsJson
    rulesVersion
    analyzedAt
    slides {
      ${PITCH_DECK_AUDIT_SLIDE_FIELDS}
    }
  }
`

/** Full deck graph used by the shell / overview / slide detail. */
export const PITCH_DECK_FIELDS = gql`
  fragment PitchDeckAuditorDeckFields on ScoPitchDeck {
    id
    pitchDeckId
    title
    statusKey
    phaseKey
    slideCount
    analyzedAt
    rulesVersion
    errorAsJson
    latestPitchDeckAuditId
    auditAiActionId
    classifyAiActionId
    deckStoredFileId
    deckStoredFile {
      id
      storedFileId
      title
      storageFileName
      fullUrl
      storageFileSizeInBytes
      mimeType
    }
    overallToolSession {
      id
      toolSessionId
      threadId
    }
    latestPitchDeckAudit {
      ${PITCH_DECK_AUDIT_FIELDS}
    }
    slides {
      ${PITCH_DECK_SLIDE_FIELDS}
    }
  }
`

export const SCO_PITCH_DECK_QUERY = gql`
  query ScoPitchDeckForAuditor($pitchDeckId: String!) {
    scoPitchDeck(pitchDeckId: $pitchDeckId) {
      ${PITCH_DECK_FIELDS}
    }
  }
`

export const MY_SCO_PITCH_DECKS_QUERY = gql`
  query MyScoPitchDecks($userId: String, $page: Int, $pageSize: Int) {
    scoPitchDecks(userId: $userId, page: $page, pageSize: $pageSize) {
      id
      pitchDeckId
      title
      statusKey
      phaseKey
      slideCount
      analyzedAt
      createdAt
      latestPitchDeckAudit {
        id
        pitchDeckAuditId
        overallScore
        wilVerdict
        companyName
      }
      slides {
        id
        pitchDeckSlideId
        position
        slideImageUrl
      }
    }
  }
`

export const PITCH_DECK_STATIC_OPTIONS_QUERY = gql`
  query PitchDeckStaticOptions {
    scoStaticOptions {
      pitchDeckSlideTypes {
        id
        key
        title
        type
        strength
        colorHexCode
      }
      pitchDeckRatings {
        id
        key
        title
        colorHexCode
        strength
      }
      pitchDeckRules {
        id
        key
        title
        type
        strength
      }
      pitchDeckCoherenceTypes {
        id
        key
        title
        description
      }
    }
  }
`

export const SCO_PITCH_DECK_UPDATED_SUBSCRIPTION = gql`
  subscription ScoPitchDeckUpdatedForAuditor($pitchDeckId: String!) {
    scoPitchDeckUpdated(pitchDeckId: $pitchDeckId) {
      id
      pitchDeckId
      pitchDeck {
        id
        pitchDeckId
        statusKey
        phaseKey
        slideCount
        analyzedAt
        errorAsJson
        latestPitchDeckAuditId
        auditAiActionId
        classifyAiActionId
        overallToolSession {
          id
          toolSessionId
          threadId
        }
        slides {
          ${PITCH_DECK_SLIDE_FIELDS}
        }
      }
    }
  }
`

export const SCO_PITCH_DECK_AUDIT_UPDATED_SUBSCRIPTION = gql`
  subscription ScoPitchDeckAuditUpdatedForAuditor($pitchDeckAuditId: String!) {
    scoPitchDeckAuditUpdated(pitchDeckAuditId: $pitchDeckAuditId) {
      id
      pitchDeckAuditId
      pitchDeckAudit {
        ${PITCH_DECK_AUDIT_FIELDS}
      }
    }
  }
`

export const UPSERT_SCO_PITCH_DECK_MUTATION = gql`
  mutation UpsertScoPitchDeck($input: UpsertScoPitchDeckInput!) {
    upsertScoPitchDeck(input: $input) {
      pitchDeck {
        id
        pitchDeckId
        statusKey
        phaseKey
      }
    }
  }
`

export const REANALYZE_SCO_PITCH_DECK_MUTATION = gql`
  mutation ReanalyzeScoPitchDeck($input: ReanalyzeScoPitchDeckInput!) {
    reanalyzeScoPitchDeck(input: $input) {
      pitchDeck {
        id
        pitchDeckId
        statusKey
        phaseKey
        latestPitchDeckAuditId
      }
      pitchDeckAudit {
        id
        pitchDeckAuditId
        statusKey
      }
    }
  }
`

export const UPSERT_SCO_PITCH_DECK_AUDIT_SLIDE_MUTATION = gql`
  mutation UpsertScoPitchDeckAuditSlide($input: UpsertScoPitchDeckAuditSlideInput!) {
    upsertScoPitchDeckAuditSlide(input: $input) {
      pitchDeckAuditSlide {
        id
        pitchDeckAuditSlideId
        acceptedRewrite
        acceptedAt
      }
    }
  }
`

export const ENSURE_SCO_PITCH_DECK_SLIDE_CHAT_MUTATION = gql`
  mutation EnsureScoPitchDeckSlideChat($input: EnsureScoPitchDeckSlideChatInput!) {
    ensureScoPitchDeckSlideChat(input: $input) {
      pitchDeckSlide {
        id
        pitchDeckSlideId
        toolSession {
          id
          toolSessionId
          threadId
        }
      }
    }
  }
`
